// Map-control engine.
//
// Produces, per computed frame, a per-cell state:
//   0 neutral | 1 T hard | 2 T implicit | 3 CT hard | 4 CT implicit | 5 contested
//
// Ownership is "who can get there first" over the map's real traversability graph, which is derived
// offline from the CS2 nav mesh. Solid (hard) means a body is on it or the team is effectively
// already standing there; hatched (implicit) means it is theirs but further out. Territory the enemy
// cannot reach at all without crossing our hard space counts as ours regardless of distance.
//
// Implementation notes worth keeping:
//  - Stride is measured in TICKS, never frames. The collector captures at the demo packet rate,
//    which is 64Hz in practice, so a frame-count stride would be wrong by whatever the real rate is.
//  - `dist` must be Float64. The heap holds float64 costs; comparing them against a float32-rounded
//    dist[u] makes the stale-entry guard misfire and silently truncates the search.
//  - Dead players are filtered on the isAlive flag, NOT on the (0,0) sentinel: a pawn that still
//    exists with Health=0 serialises its corpse position with the alive bit clear.

import type { Frame, RosterEntry } from '../replayParser';
import type { ControlGrid } from './mapControlGrid';

export const S_NEUTRAL = 0, S_T_HARD = 1, S_T_IMP = 2, S_CT_HARD = 3, S_CT_IMP = 4, S_CONTEST = 5;

const RUN_SPEED = 250;      // world units/sec
const HARD_SECS = 2.0;      // within this travel time, treat as firmly held (solid)
const CONTEST_SECS = 0.9;   // arrival-time margin before a cell counts as contested
const NEUTRAL_SECS = 6.5;   // reach beyond this is too far to claim ground outright
const PROTECT_SECS = 6;     // protected ground must be near enough that you could actually hold it
const PROOF_TICKS = 128;    // 2s at 64Hz of continuous proof before implicit fades in
// Proof DECAYS rather than resetting. Zeroing it meant a single wobbly frame cost a full 2s to
// re-arm, so held ground visibly blinked out and back roughly nine times a second.
const PROOF_DECAY = 2;      // ticks of proof lost per tick unsupported
const BODY_RADIUS = 96;
const FIGHT_TICKS = 64;
const FLIP_TICKS = 48;
// Ground is CLAIMED by OCCUPYING it (see occT/occCT), never by proximity or by merely looking down
// a lane, and then stays yours for 25s unless the enemy takes it. Swept against the reference mix.
const HOLD_TICKS = 64 * 25;
// Share of pending territory losses released per computed step, furthest from the owner first.
// Makes a region peel back over a couple of seconds rather than disappearing in one frame.
const RELEASE_FRACTION = 0.04;
const SMOKE_COST = 2.2;     // crossable but slow and risky
const FIRE_COST = 12;       // effectively denied
const SMOKE_RADIUS = 150, SMOKE_DURATION_TICKS = 64 * 18, FLAME_RADIUS = 48;
const INF = 1e9;

// Vision ("a gun trained down it"). Requires the v3 asset's precomputed visibility.
// A *lane*, not a field of view. 104 degrees of awareness let five players mark 90% of a map as
// hard-controlled, which then read as contested everywhere the two teams overlapped.
const FOV_HALF = Math.cos((14 * Math.PI) / 180);   // ~28 degrees: a gun actually trained down it
const YAW_STABLE_DEG = 22;   // beyond this between samples the player is sweeping, not holding
const VIEW_RANGE = 1600;                          // world units
const LANE_MARGIN = 0.75;  // a lane cannot claim ground the enemy reaches sooner than this
const VISION_HOLD_TICKS = 40;                     // ~0.6s, so sweeping a crosshair doesn't strobe

export interface ShotMark { x: number; y: number; z?: number }

export interface MapControlEngine {
    /** Number of walkable cells (compact index space). */
    readonly M: number;
    /** compact index -> full grid index */
    readonly cells: Int32Array;
    readonly grid: ControlGrid;
    reset(): void;
    step(frame: Frame, roster: RosterEntry[], tick: number, dtTicks: number, shots: ShotMark[]): Uint8Array;
    share(states: Uint8Array): { t: number; ct: number; contested: number };
}

export function createMapControlEngine(grid: ControlGrid): MapControlEngine {
    const { G, cell, cfg, data } = grid;
    const N = G * G;
    const DX = [0, 1, 0, -1], DY = [-1, 0, 1, 0];

    // compact index over walkable cells only
    const cid = new Int32Array(N).fill(-1);
    const cellList: number[] = [];
    for (let c = 0; c < N; c++) if (data[c] & 1) { cid[c] = cellList.length; cellList.push(c); }
    const cells = Int32Array.from(cellList);
    const M = cells.length;

    // CSR neighbour lists
    const nbrOff = new Int32Array(M + 1);
    const nbrTmp: number[] = [];
    for (let i = 0; i < M; i++) {
        nbrOff[i] = nbrTmp.length;
        const c = cells[i], gx = c % G, gy = (c - (c % G)) / G;
        for (let d = 0; d < 4; d++) {
            if (!(data[c] & (1 << (d + 1)))) continue;
            const nx = gx + DX[d], ny = gy + DY[d];
            if (nx < 0 || ny < 0 || nx >= G || ny >= G) continue;
            const j = cid[ny * G + nx];
            if (j >= 0) nbrTmp.push(j);
        }
    }
    nbrOff[M] = nbrTmp.length;
    const nbr = Int32Array.from(nbrTmp);

    const distT = new Float64Array(M), distCT = new Float64Array(M);
    const terrain = new Float32Array(M);
    const hardT = new Uint8Array(M), hardCT = new Uint8Array(M);
    // Hard control from BODIES only (before firing lanes are folded in). A lasting claim on ground
    // comes from having actually been there; merely looking down a lane should not claim territory
    // for the next 45 seconds, and lanes cover far more area than bodies do.
    const occT = new Uint8Array(M), occCT = new Uint8Array(M);
    const reachT = new Uint8Array(M), reachCT = new Uint8Array(M);
    // Ground a team can actually SEE from where they stand (union of their visibility, ignoring
    // where they happen to be aiming). Reach alone is not control: you may be able to walk somewhere
    // quickly and still have no sight of it, which is how a single player ended up implicitly
    // holding open ground hidden behind boxes.
    const seenT = new Uint8Array(M), seenCT = new Uint8Array(M);
    const proofT = new Uint16Array(M), proofCT = new Uint16Array(M);
    const lastOwner = new Uint8Array(M);
    const lastHardOwner = new Uint8Array(M);
    // Deadline until which unsupported territory keeps its owner. Without an expiry an early-round
    // claim would hold all round and the whole map ends up owned.
    const lastOcc = new Int32Array(M).fill(-1 << 30);
    // A claim is pure MEMORY of having occupied the ground, aged per cell. It must not depend on
    // where the team is now: distance jumps discontinuously when a player dies or changes floor,
    // which made every claim in a region expire on the same frame and whole areas blink out.
    // Ageing per cell means ground occupied at different times also expires at different times, so
    // territory recedes gradually instead of vanishing as one block.
    // Cells currently held by a firing lane, with a short hold so a swept crosshair doesn't flicker.
    const laneT = new Int32Array(M).fill(-1 << 30), laneCT = new Int32Array(M).fill(-1 << 30);
    // Previous-sample yaw per player, to tell holding an angle from sweeping the crosshair.
    const prevYaw = new Float32Array(64).fill(NaN);
    // Precomputed world centre of each walkable cell, for the vision cone test.
    const cellWX = new Float32Array(M), cellWY = new Float32Array(M);
    for (let i = 0; i < M; i++) {
        const c = cells[i], gx = c % G, gy = (c - (c % G)) / G;
        cellWX[i] = cfg.pos_x + (gx + 0.5) * cell;
        cellWY[i] = cfg.pos_y - (gy + 0.5) * cell;
    }
    const hasVision = grid.pvsStride > 0 && grid.cellPoly.length === N && grid.pvs.length > 0;
    let polyState = new Uint8Array(0), polySmoothed = new Uint8Array(0);
    let releaseCredit = new Float32Array(0);
    let prevPoly: Uint8Array | null = null;

    // polygon -> its walkable cells, as CSR. Serves two purposes: the vision pass walks only the
    // ~9% of polygons a player can see, and the output is resolved per polygon so regions read as
    // coherent shapes instead of per-cell speckle.
    let polyOff = new Int32Array(0), polyCells = new Int32Array(0), polyCount = 0;
    let adjOff = new Int32Array(0), adjList = new Int32Array(0);
    const hasPolys = grid.cellPoly.length === N && grid.polygons.length > 0;
    if (hasPolys) {
        polyCount = grid.polygons.length;
        const counts = new Int32Array(polyCount + 1);
        for (let i = 0; i < M; i++) {
            const p = grid.cellPoly[cells[i]];
            if (p !== 0xFFFF && p < polyCount) counts[p]++;
        }
        polyOff = new Int32Array(polyCount + 1);
        for (let p = 0; p < polyCount; p++) polyOff[p + 1] = polyOff[p] + counts[p];
        polyCells = new Int32Array(polyOff[polyCount]);
        const cursor = Int32Array.from(polyOff.subarray(0, polyCount));
        for (let i = 0; i < M; i++) {
            const p = grid.cellPoly[cells[i]];
            if (p !== 0xFFFF && p < polyCount) polyCells[cursor[p]++] = i;
        }

        // polygon adjacency, derived from cell adjacency
        const pairs = new Set<number>();
        for (let i = 0; i < M; i++) {
            const a = grid.cellPoly[cells[i]];
            if (a === 0xFFFF) continue;
            for (let k = nbrOff[i]; k < nbrOff[i + 1]; k++) {
                const b = grid.cellPoly[cells[nbr[k]]];
                if (b === 0xFFFF || b === a) continue;
                pairs.add(a < b ? a * polyCount + b : b * polyCount + a);
            }
        }
        const deg = new Int32Array(polyCount + 1);
        pairs.forEach(v => { const a = Math.floor(v / polyCount), b = v % polyCount; deg[a]++; deg[b]++; });
        adjOff = new Int32Array(polyCount + 1);
        for (let p2 = 0; p2 < polyCount; p2++) adjOff[p2 + 1] = adjOff[p2] + deg[p2];
        adjList = new Int32Array(adjOff[polyCount]);
        polyState = new Uint8Array(polyCount); polySmoothed = new Uint8Array(polyCount);
        releaseCredit = new Float32Array(polyCount);
        const cur2 = Int32Array.from(adjOff.subarray(0, polyCount));
        pairs.forEach(v => {
            const a = Math.floor(v / polyCount), b = v % polyCount;
            adjList[cur2[a]++] = b; adjList[cur2[b]++] = a;
        });
    }
    const lastFlip = new Int32Array(M).fill(-1 << 30);
    const fightUntil = new Int32Array(M).fill(-1 << 30);
    const stepCost = cell / RUN_SPEED;
    const decay = (v: number, dt: number) => { const d = v - dt * PROOF_DECAY; return d > 0 ? d : 0; };

    const cellOfWorld = (wx: number, wy: number): number => {
        const gx = Math.floor((wx - cfg.pos_x) / cell);
        const gy = Math.floor((cfg.pos_y - wy) / cell);
        if (gx < 0 || gy < 0 || gx >= G || gy >= G) return -1;
        return cid[gy * G + gx];
    };

    // A player standing where the rasterised grid happens to have no cell — the lip of a vent, the
    // edge of a doorway, the corner of a prop — used to be dropped ENTIRELY: no Dijkstra source, no
    // firing lane, no territory, as if they were not in the round. It looks like a player sitting in
    // the open generating nothing at all. Measured across five maps, that silently discarded
    // 2.9%–6.6% of alive-player samples, and essentially every one of them was within a single cell
    // of walkable ground. Snap to the nearest instead; give up only if nothing is close, which means
    // they really are off the playable area.
    const SNAP_CELLS = 2;
    const cellOfWorldNear = (wx: number, wy: number): number => {
        const direct = cellOfWorld(wx, wy);
        if (direct >= 0) return direct;
        const gx = Math.floor((wx - cfg.pos_x) / cell);
        const gy = Math.floor((cfg.pos_y - wy) / cell);
        for (let r = 1; r <= SNAP_CELLS; r++) {
            let best = -1, bestD = Infinity;
            for (let dy = -r; dy <= r; dy++) {
                for (let dx = -r; dx <= r; dx++) {
                    if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue;
                    const nx = gx + dx, ny = gy + dy;
                    if (nx < 0 || ny < 0 || nx >= G || ny >= G) continue;
                    const j = cid[ny * G + nx];
                    if (j < 0) continue;
                    const d = dx * dx + dy * dy;
                    if (d < bestD) { bestD = d; best = j; }
                }
            }
            if (best >= 0) return best;
        }
        return -1;
    };

    // binary-heap Dijkstra over the traversability graph
    const heapD: number[] = [], heapN: number[] = [];
    function hpush(d: number, n: number) {
        heapD.push(d); heapN.push(n);
        let i = heapD.length - 1;
        while (i > 0) {
            const p = (i - 1) >> 1;
            if (heapD[p] <= heapD[i]) break;
            [heapD[p], heapD[i]] = [heapD[i], heapD[p]];
            [heapN[p], heapN[i]] = [heapN[i], heapN[p]];
            i = p;
        }
    }
    function hpop(): [number, number] {
        const d = heapD[0], n = heapN[0];
        const ld = heapD.pop()!, ln = heapN.pop()!;
        if (heapD.length) {
            heapD[0] = ld; heapN[0] = ln;
            let i = 0;
            for (;;) {
                const l = 2 * i + 1, r = l + 1;
                let s = i;
                if (l < heapD.length && heapD[l] < heapD[s]) s = l;
                if (r < heapD.length && heapD[r] < heapD[s]) s = r;
                if (s === i) break;
                [heapD[s], heapD[i]] = [heapD[i], heapD[s]];
                [heapN[s], heapN[i]] = [heapN[i], heapN[s]];
                i = s;
            }
        }
        return [d, n];
    }

    function multiDijkstra(sources: number[], dist: Float64Array) {
        dist.fill(INF);
        heapD.length = 0; heapN.length = 0;
        for (const s of sources) if (s >= 0 && dist[s] > 0) { dist[s] = 0; hpush(0, s); }
        while (heapD.length) {
            const [d, u] = hpop();
            if (d > dist[u]) continue;
            for (let k = nbrOff[u]; k < nbrOff[u + 1]; k++) {
                const v = nbr[k];
                const nd = d + stepCost * terrain[v];
                if (nd < dist[v]) { dist[v] = nd; hpush(nd, v); }
            }
        }
    }

    const queue = new Int32Array(M);
    function floodBlocked(sources: number[], blocked: Uint8Array, out: Uint8Array) {
        out.fill(0);
        let head = 0, tail = 0;
        // Seed even inside blocked space: a player who pushed into enemy hard control still
        // projects reach outward rather than being erased.
        for (const s of sources) if (s >= 0 && !out[s]) { out[s] = 1; queue[tail++] = s; }
        while (head < tail) {
            const u = queue[head++];
            for (let k = nbrOff[u]; k < nbrOff[u + 1]; k++) {
                const v = nbr[k];
                if (out[v] || blocked[v]) continue;
                out[v] = 1; queue[tail++] = v;
            }
        }
    }

    function stampDisc(centre: number, radiusWorld: number, apply: (j: number) => void) {
        if (centre < 0) return;
        const c = cells[centre], gx = c % G, gy = (c - (c % G)) / G;
        const r = Math.ceil(radiusWorld / cell), r2 = r * r;
        for (let dy = -r; dy <= r; dy++) {
            const ny = gy + dy;
            if (ny < 0 || ny >= G) continue;
            for (let dx = -r; dx <= r; dx++) {
                if (dx * dx + dy * dy > r2) continue;
                const nx = gx + dx;
                if (nx < 0 || nx >= G) continue;
                const j = cid[ny * G + nx];
                if (j >= 0) apply(j);
            }
        }
    }

    function reset() {
        prevPoly = null;
        releaseCredit.fill(0);
        proofT.fill(0); proofCT.fill(0); lastOwner.fill(0); lastHardOwner.fill(0); lastOcc.fill(-1 << 30); prevYaw.fill(NaN);
        lastFlip.fill(-1 << 30); fightUntil.fill(-1 << 30);
    }

    function step(frame: Frame, roster: RosterEntry[], tick: number, dtTicks: number, shots: ShotMark[]): Uint8Array {
        terrain.fill(1);
        for (const sm of frame.world.smokes) {
            const age = tick - sm.startTick;
            if (age < 0 || age > SMOKE_DURATION_TICKS) continue;
            stampDisc(cellOfWorld(sm.x, sm.y), SMOKE_RADIUS, j => {
                if (terrain[j] < SMOKE_COST) terrain[j] = SMOKE_COST;
            });
        }
        for (const fire of frame.world.fires) {
            const pts = fire.flames && fire.flames.length ? fire.flames : [{ x: fire.x, y: fire.y }];
            for (const f of pts) {
                stampDisc(cellOfWorld(f.x, f.y), FLAME_RADIUS, j => {
                    if (terrain[j] < FIRE_COST) terrain[j] = FIRE_COST;
                });
            }
        }

        hardT.fill(0); hardCT.fill(0);
        const srcT: number[] = [], srcCT: number[] = [];
        for (let i = 0; i < roster.length; i++) {
            const p = frame.players[i];
            if (!p || !p.isAlive || (p.x === 0 && p.y === 0)) continue;
            const c = cellOfWorldNear(p.x, p.y);
            if (c < 0) continue;
            const isT = roster[i].team === 'T';
            (isT ? srcT : srcCT).push(c);
            // A body only cuts the map if it can actually contest it. Knife/nade-only or badly hurt
            // players block just their own cell, so save rounds don't claim everything behind them.
            const canContest = p.hp >= 30 && p.weaponId !== 0
                && !(p.weaponId >= 70 && p.weaponId <= 75) && p.weaponId !== 60 && p.weaponId !== 61;
            const target = isT ? hardT : hardCT;
            stampDisc(c, canContest ? BODY_RADIUS : cell * 0.9, j => { target[j] = 1; });
        }

        multiDijkstra(srcT, distT);
        multiDijkstra(srcCT, distCT);
        for (let i = 0; i < M; i++) {
            if (distT[i] <= HARD_SECS) hardT[i] = 1;
            if (distCT[i] <= HARD_SECS) hardCT[i] = 1;
        }

        occT.set(hardT); occCT.set(hardCT);

        // --- firing lanes: ground a player can actually SEE and is looking at, with a gun out ---
        if (hasVision) {
            for (let i = 0; i < roster.length; i++) {
                const p = frame.players[i];
                if (!p || !p.isAlive || (p.x === 0 && p.y === 0)) continue;
                if (p.flags & (1 << 8)) continue;                       // blinded
                const wid = p.weaponId;
                if (!wid || wid === 60 || wid === 61 || (wid >= 70 && wid <= 80)) continue;  // no gun out

                // Holding, not sweeping: a crosshair swinging across the map does not control it.
                const yawPrev = prevYaw[i];
                prevYaw[i] = p.yaw;
                if (!Number.isNaN(yawPrev)) {
                    let dYaw = Math.abs(p.yaw - yawPrev) % 360;
                    if (dYaw > 180) dYaw = 360 - dYaw;
                    if (dYaw > YAW_STABLE_DEG) continue;
                }
                const from = cellOfWorldNear(p.x, p.y);
                if (from < 0) continue;
                const poly = grid.cellPoly[cells[from]];
                if (poly === 0xFFFF) continue;

                const row = poly * grid.pvsStride;
                const yaw = (p.yaw * Math.PI) / 180;
                const fx = Math.cos(yaw), fy = Math.sin(yaw);
                const lane = roster[i].team === 'T' ? laneT : laneCT;

                const R2 = VIEW_RANGE * VIEW_RANGE;
                for (let q = 0; q < polyCount; q++) {
                    if (!(grid.pvs[row + (q >> 3)] & (1 << (q & 7)))) continue;   // polygon not visible
                    for (let k = polyOff[q]; k < polyOff[q + 1]; k++) {
                        const j = polyCells[k];
                        const dx = cellWX[j] - p.x, dy = cellWY[j] - p.y;
                        const d2 = dx * dx + dy * dy;
                        if (d2 > R2) continue;
                        if (d2 > 1 && (dx * fx + dy * fy) < FOV_HALF * Math.sqrt(d2)) continue;
                        lane[j] = tick;
                    }
                }
            }
            // Union of everything each side can see, used to gate reach-based implicit control.
            seenT.fill(0); seenCT.fill(0);
            for (let i2 = 0; i2 < roster.length; i2++) {
                const pl = frame.players[i2];
                if (!pl || !pl.isAlive || (pl.x === 0 && pl.y === 0)) continue;
                const from = cellOfWorldNear(pl.x, pl.y);
                if (from < 0) continue;
                const poly = grid.cellPoly[cells[from]];
                if (poly === 0xFFFF) continue;
                const row = poly * grid.pvsStride;
                const dst = roster[i2].team === 'T' ? seenT : seenCT;
                for (let q = 0; q < polyCount; q++) {
                    if (!(grid.pvs[row + (q >> 3)] & (1 << (q & 7)))) continue;
                    for (let k = polyOff[q]; k < polyOff[q + 1]; k++) dst[polyCells[k]] = 1;
                }
                for (let k = polyOff[poly]; k < polyOff[poly + 1]; k++) dst[polyCells[k]] = 1;
            }

            // Seeing into enemy ground is not controlling it: a lane only holds space the team
            // could also plausibly contest, so it cannot claim territory deep behind enemy lines.
            // The `< INF` guard is load-bearing. Ground nobody can walk to — the top of silo, a
            // ledge, a roof — has an infinite distance for BOTH sides, which made the margin test
            // `INF <= INF + margin` trivially true, so merely looking at scenery claimed it and
            // (via lastOcc) held it for 25s. You cannot control ground you cannot reach.
            for (let j = 0; j < M; j++) {
                if (tick - laneT[j] <= VISION_HOLD_TICKS && distT[j] < INF && distT[j] <= distCT[j] + LANE_MARGIN) hardT[j] = 1;
                if (tick - laneCT[j] <= VISION_HOLD_TICKS && distCT[j] < INF && distCT[j] <= distT[j] + LANE_MARGIN) hardCT[j] = 1;
            }
        }



        floodBlocked(srcCT, hardT, reachCT);
        floodBlocked(srcT, hardCT, reachT);

        for (const s of shots) {
            stampDisc(cellOfWorld(s.x, s.y), 96, j => { fightUntil[j] = tick + FIGHT_TICKS; });
        }

        const bothPresent = srcT.length > 0 && srcCT.length > 0;
        const out = new Uint8Array(M);
        for (let i = 0; i < M; i++) {
            const dT = distT[i], dC = distCT[i];
            // Ground you have taken stays yours until the enemy could plausibly take it back.
            // Only space nobody can reach, or that nobody has ever established, is neutral —
            // otherwise walking past your own territory would instantly surrender it.
            if (dT >= INF && dC >= INF) {
                // Ground nobody can currently reach. This is NOT proof that nobody owns it: the
                // rasterised grid is fragmented (de_nuke's upper level alone splits into 34
                // components, with the whole western corridor disconnected from the main map), so a
                // player crossing a seam makes everything beyond it unreachable for a moment.
                // Clearing lastOwner here annihilated a 1200-cell region in a single frame while its
                // owner was still standing in it. Keep the claim and let it age out like any other.
                if (lastOwner[i] && tick - lastOcc[i] < HOLD_TICKS) {
                    const heldByT = lastOwner[i] === 1;
                    const proof = heldByT ? proofT : proofCT;
                    proof[i] = Math.min(65535, proof[i] + dtTicks);
                    out[i] = proof[i] >= PROOF_TICKS ? (heldByT ? S_T_IMP : S_CT_IMP) : S_NEUTRAL;
                } else {
                    proofT[i] = decay(proofT[i], dtTicks); proofCT[i] = decay(proofCT[i], dtTicks);
                    out[i] = S_NEUTRAL;
                }
                continue;
            }
            // NOTE: there is deliberately no absolute "both sides are far, so it's neutral" gate.
            // That cutoff dropped ground the moment both teams were >NEUTRAL_SECS away, even when
            // one side was obviously the closer one, which made held territory blink out. Whether a
            // side has a claim is decided below, from reach *relative* to the enemy plus what they
            // have actually established.

            // Hard control decides the cell outright: a body on it, or a live firing lane, beats
            // being marginally closer to it. Only when BOTH sides actively hold a cell is it
            // genuinely contested.
            const hT = hardT[i], hC = hardCT[i];
            let st: number;
            if (hT && hC) {
                proofT[i] = decay(proofT[i], dtTicks); proofCT[i] = decay(proofCT[i], dtTicks);
                st = S_CONTEST;
            } else if (hT) {
                proofT[i] = Math.min(65535, proofT[i] + dtTicks); proofCT[i] = decay(proofCT[i], dtTicks);
                st = S_T_HARD;
            } else if (hC) {
                proofCT[i] = Math.min(65535, proofCT[i] + dtTicks); proofT[i] = decay(proofT[i], dtTicks);
                st = S_CT_HARD;
            } else {
                // Nobody actively holds it — fall back to who would get there first.
                // "Protected" means the enemy cannot get in without going through me — which only
                // makes sense for ground BEHIND my own line. Without the distance condition, cutting
                // a corridor made all the space beyond it unreachable for the enemy, and the side
                // that was 20 seconds away claimed it: CTs ended up owning T spawn.
                // Protected ground must be ground I can actually get to. Testing only "the enemy
                // cannot reach it" handed a side any pocket that was disconnected from the ENEMY's
                // graph: with dT infinite, `dC <= dT` is trivially true, so CT was awarded T spawn
                // from 27 seconds away. Requiring real proximity makes an unreachable pocket
                // neutral, which is what it is.
                const protT = !reachCT[i] && dT <= PROTECT_SECS && dT <= dC;
                const protC = !reachT[i] && dC <= PROTECT_SECS && dC <= dT;
                // A side has a claim if it established the ground earlier and still holds the claim,
                // or if it is both clearly closer AND close enough for that to mean anything.
                // Ground neither side has taken, and neither is near, stays neutral.
                const age = tick - lastOcc[i];
                const claimT = lastOwner[i] === 1 && age < HOLD_TICKS;
                const claimC = lastOwner[i] === 2 && age < HOLD_TICKS;
                // Reach-based ownership additionally requires sight of the ground. Without this a
                // player implicitly held open areas they could jog to but could not see into.
                const nearT = dT <= NEUTRAL_SECS && dT < dC - CONTEST_SECS && (!hasVision || seenT[i]);
                const nearC = dC <= NEUTRAL_SECS && dC < dT - CONTEST_SECS && (!hasVision || seenCT[i]);
                const tOwns = protT || claimT || nearT;
                const cOwns = protC || claimC || nearC;
                if (tOwns && !cOwns) {
                    proofT[i] = Math.min(65535, proofT[i] + dtTicks); proofCT[i] = decay(proofCT[i], dtTicks);
                    st = proofT[i] >= PROOF_TICKS ? S_T_IMP : S_NEUTRAL;
                } else if (cOwns && !tOwns) {
                    proofCT[i] = Math.min(65535, proofCT[i] + dtTicks); proofT[i] = decay(proofT[i], dtTicks);
                    st = proofCT[i] >= PROOF_TICKS ? S_CT_IMP : S_NEUTRAL;
                } else if (lastOwner[i] && (tick - lastOcc[i]) < HOLD_TICKS) {
                    // Equidistant but previously taken: it stays with whoever held it. Territory is
                    // only lost when the enemy actually gets there first, not the moment you leave.
                    const heldByT = lastOwner[i] === 1;
                    const proof = heldByT ? proofT : proofCT;
                    proof[i] = Math.min(65535, proof[i] + dtTicks);
                    st = proof[i] >= PROOF_TICKS ? (heldByT ? S_T_IMP : S_CT_IMP) : S_NEUTRAL;
                } else {
                    // Never held by anyone and equidistant: genuine no-man's-land.
                    proofT[i] = decay(proofT[i], dtTicks); proofCT[i] = decay(proofCT[i], dtTicks);
                    st = S_NEUTRAL;
                }
            }

            // "changing hands" is a recent flip, not merely sequential visitation
            const own = (st === S_T_HARD || st === S_T_IMP) ? 1 : (st === S_CT_HARD || st === S_CT_IMP) ? 2 : 0;
            // Only a HARD handover is "changing hands". Implicit ownership drifting as distances
            // shift is not a fight, and marking it red paints the map with false contest.
            const hardOwn = st === S_T_HARD ? 1 : st === S_CT_HARD ? 2 : 0;
            // Ground only "changes hands" if the side losing it is actually present to lose it.
            // On a split-level map a team that has gone downstairs is not contesting the floor above.
            if (bothPresent && hardOwn && lastHardOwner[i] && hardOwn !== lastHardOwner[i]) lastFlip[i] = tick;
            if (hardOwn) lastHardOwner[i] = hardOwn;
            if (own) {
                lastOwner[i] = own;
                // only ACTIVE support refreshes the hold; coasting on memory must not extend it
                // Ground is claimed by HOLDING it — standing on it, or having a gun trained down it.
                // Body-only stamping meant a player who cleared and held a corridor claimed just the
                // ~96-unit disc under their feet, so the space they actually controlled had no memory
                // and evaporated the moment their crosshair moved. Merely being ABLE to walk
                // somewhere still claims nothing; that is the difference between reach and control.
                if (occT[i] || occCT[i] || hardT[i] || hardCT[i]) lastOcc[i] = tick;
            }
            if (tick - lastFlip[i] < FLIP_TICKS) st = S_CONTEST;
            if (tick < fightUntil[i]) st = S_CONTEST;

            out[i] = st;
        }

        // --- resolve to coherent regions -------------------------------------------------------
        // Control is computed per 32u cell but read per nav polygon, so without this the two
        // disagree and the map renders as speckle. Take each polygon's majority state, then smooth
        // once across polygon adjacency (area-weighted) so a lone polygon surrounded by one side
        // joins it rather than punching a hole.
        if (hasPolys && polyCount > 0) {
            const hist = new Int32Array(6);
            for (let p2 = 0; p2 < polyCount; p2++) {
                const s = polyOff[p2], e = polyOff[p2 + 1];
                if (s === e) { polyState[p2] = S_NEUTRAL; continue; }
                hist.fill(0);
                for (let k = s; k < e; k++) hist[out[polyCells[k]]]++;
                let best = S_NEUTRAL, bc = -1;
                for (let v = 0; v < 6; v++) if (hist[v] > bc) { bc = hist[v]; best = v; }
                polyState[p2] = best;
            }

            // Two smoothing passes, area-weighted over polygon adjacency. Nav polygons are small, so
            // one pass still leaves a busy, hard-to-read picture; two merges them into the larger
            // shapes a viewer can actually parse at a glance. The self-bias keeps a genuinely
            // distinct region from being swallowed by its surroundings.
            for (let pass = 0; pass < 2; pass++) {
                const src = pass === 0 ? polyState : polySmoothed;
                const dst = pass === 0 ? polySmoothed : polyState;
                for (let p2 = 0; p2 < polyCount; p2++) {
                    hist.fill(0);
                    const own = src[p2];
                    hist[own] += (polyOff[p2 + 1] - polyOff[p2]) * 2 + 1;
                    for (let k = adjOff[p2]; k < adjOff[p2 + 1]; k++) {
                        const q = adjList[k];
                        hist[src[q]] += polyOff[q + 1] - polyOff[q];
                    }
                    let best = own, bc = -1;
                    for (let v = 0; v < 6; v++) if (hist[v] > bc) { bc = hist[v]; best = v; }
                    dst[p2] = best;
                }
            }
            polySmoothed.set(polyState);   // second pass wrote into polyState

            // --- territory recedes furthest-first ---------------------------------------------
            // Ground is not surrendered all at once: the far end goes first and the edge pulls back
            // toward whoever holds it. Ownership derives from distance fields that jump when a
            // player dies or changes floor, so without this a whole region expires on one frame.
            if (prevPoly) {
                const losing: number[] = [];
                for (let p2 = 0; p2 < polyCount; p2++) {
                    const was = prevPoly[p2], now = polySmoothed[p2];
                    const wasOwner = was === S_T_HARD || was === S_T_IMP ? 1 : was === S_CT_HARD || was === S_CT_IMP ? 2 : 0;
                    if (!wasOwner) continue;
                    const nowOwner = now === S_T_HARD || now === S_T_IMP ? 1 : now === S_CT_HARD || now === S_CT_IMP ? 2 : 0;
                    if (nowOwner === wasOwner) continue;
                    // An ACTIVE takeover applies at once: if the enemy is standing on it, or has a
                    // gun trained down it, or it is being fought over, they have taken it now. Only
                    // passive decay - ground fading because nobody supports it any more - is paced.
                    if (now === S_T_HARD || now === S_CT_HARD || now === S_CONTEST) continue;
                    losing.push(p2);
                }
                if (losing.length) {
                    // distance of each polygon from the side about to lose it
                    const distOf = (p2: number) => {
                        const was = prevPoly![p2];
                        const isT = was === S_T_HARD || was === S_T_IMP;
                        const src = isT ? distT : distCT;
                        let sum = 0, n = 0;
                        for (let k = polyOff[p2]; k < polyOff[p2 + 1]; k++) { const d = src[polyCells[k]]; if (d < INF) { sum += d; n++; } }
                        return n ? sum / n : Infinity;   // unreachable ground goes first
                    };
                    const rank = new Map<number, number>();
                    let losingArea = 0;
                    for (const p2 of losing) { rank.set(p2, distOf(p2)); losingArea += polyOff[p2 + 1] - polyOff[p2]; }
                    losing.sort((a, b) => rank.get(b)! - rank.get(a)!);   // furthest first

                    // Budget in AREA, not polygon count: nav polygons differ in size by orders of
                    // magnitude, so "release 2 polygons" can hand over most of the map at once.
                    // Each losing polygon accrues credit toward its own area and flips only once
                    // fully paid, so a large one recedes proportionally later than a small one
                    // instead of stalling forever.
                    let budget = Math.max(1, losingArea * RELEASE_FRACTION);
                    for (const p2 of losing) {
                        const size = polyOff[p2 + 1] - polyOff[p2];
                        if (budget > 0) {
                            const pay = Math.min(budget, size - releaseCredit[p2]);
                            releaseCredit[p2] += pay; budget -= pay;
                        }
                        if (releaseCredit[p2] >= size) releaseCredit[p2] = 0;      // paid off: let it go
                        else polySmoothed[p2] = prevPoly[p2];                       // still holding on
                    }
                }
                // ground that is no longer being lost has its progress refunded
                const stillLosing = new Set(losing);
                for (let p2 = 0; p2 < polyCount; p2++) if (releaseCredit[p2] && !stillLosing.has(p2)) releaseCredit[p2] = 0;
            } else {
                prevPoly = new Uint8Array(polyCount);
            }
            prevPoly.set(polySmoothed);

            for (let p2 = 0; p2 < polyCount; p2++) {
                const v = polySmoothed[p2];
                for (let k = polyOff[p2]; k < polyOff[p2 + 1]; k++) out[polyCells[k]] = v;
            }
        }
        return out;
    }

    function share(states: Uint8Array) {
        let t = 0, ct = 0, k = 0;
        for (let i = 0; i < states.length; i++) {
            const s = states[i];
            if (s === S_T_HARD || s === S_T_IMP) t++;
            else if (s === S_CT_HARD || s === S_CT_IMP) ct++;
            else if (s === S_CONTEST) k++;
        }
        const n = states.length || 1;
        return { t: t / n, ct: ct / n, contested: k / n };
    }

    return { M, cells, grid, reset, step, share };
}
